import { Button, Menu, Stack, Typography } from "@mui/material";
import { useRef, useState, useEffect } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { LanguageMenuItems } from "./LanguageMenuItems";
import { useAtomValue } from "jotai";
import { languageOptionsAtom } from "atoms";
import { useTranslation } from "react-i18next";

interface LanguageButtonMenuProps {
  buttonText: string;
  onLanguageClick: (selectedLanguage: string) => void;
  currentTagLanguage?: string;
  menuZIndex?: number;
}

export default function LanguageButtonMenu({
  buttonText,
  onLanguageClick,
  currentTagLanguage,
  menuZIndex = 1300,
}: LanguageButtonMenuProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const languageOptions = useAtomValue(languageOptionsAtom);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    currentTagLanguage || "automatic"
  );

  const automaticOption = {
    value: "automatic",
    name: t("tts.toolbarMenu.languageMenu.automatic"),
  };

  const allOptions = [automaticOption, ...languageOptions];

  useEffect(() => {
    if (currentTagLanguage) {
      setSelectedLanguage(currentTagLanguage);
    } else {
      setSelectedLanguage("automatic");
    }
  }, [currentTagLanguage]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = () => {
    setOpen(true);
  };

  const handleLanguageClick = (value: string) => {
    setSelectedLanguage(value);
    onLanguageClick(value);
    setOpen(false);
  };

  const displayText = selectedLanguage
    ? allOptions.find((lang) => lang.value === selectedLanguage)?.name ||
      buttonText
    : buttonText;

  return (
    <>
      <Button
        ref={buttonRef}
        id="language-button"
        aria-controls={open ? "language-button-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        disableRipple
        sx={{
          p: 0,
          "&:hover": { backgroundColor: "transparent" },
          color: "primary.main",
          minWidth: 0,
        }}
      >
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ overflow: "hidden", maxWidth: "120px" }}
        >
          <Typography
            variant="button"
            color="primary.main"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {displayText}
          </Typography>
          {open ? (
            <ArrowDropUpIcon sx={{ flexShrink: 0 }} />
          ) : (
            <ArrowDropDownIcon sx={{ flexShrink: 0 }} />
          )}
        </Stack>
      </Button>
      <Menu
        id="language-button-menu"
        anchorEl={buttonRef.current}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "language-button",
        }}
        PaperProps={{
          sx: {
            boxShadow: "None",
            border: "1px solid #E0E0E0",
            borderRadius: "0",
          },
        }}
        slotProps={{
          root: {
            style: { zIndex: menuZIndex },
          },
        }}
      >
        <LanguageMenuItems
          onLanguageClick={(value: string) => handleLanguageClick(value)}
          currentLanguage={selectedLanguage}
          languageOptions={allOptions}
        />
      </Menu>
    </>
  );
}
